import {useCallback, useContext, useEffect, useState} from 'react';
import {SelectBox} from "../../Pickers/SelectBox/SelectBox";
import './QuestionForm.scss';
import {FormInput} from "../../Form/Input/FormInput";
import {QuestionOptions} from "../QuestionOptions/QuestionOptions";
import {RatingScaleOptions} from "../RatingScaleOptions/RatingScaleOptions";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {buttonColors} from "../../Pickers/InputTheme";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ImageIcon from '@mui/icons-material/Image';
import {Button} from "../../Form/Button/Button";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {questionnaireQuestionTypes, questionsTypesToShowOptions, surveyQuestionTypes, examQuestionTypes} from "../QuestionnaireConstants";
import {get} from "lodash";
import {QuestionnaireReactContext} from "../../../contexts/QuestionnaireContext/QuestionnaireContext";
import { useContextSelector } from 'use-context-selector';
import useDebounce from "../../../Hooks/useDebounce";
import {SignDocTypes} from '../../SignDoc/SignDocLib';
import {QuestionImageUpload} from '../QuestionImageUpload/QuestionImageUpload';

export const QuestionForm = ({onFocus, questionId}) => {
    const {i18n} = useContext(I18nReactContext);
    const deleteQuestion = useContextSelector(QuestionnaireReactContext, context => context.deleteQuestion);
    const duplicateQuestion = useContextSelector(QuestionnaireReactContext, context => context.duplicateQuestion);
    const addQuestion = useContextSelector(QuestionnaireReactContext, context => context.addQuestion);
    const updateQuestion = useContextSelector(QuestionnaireReactContext, context => context.updateQuestion);
    const question = useContextSelector(QuestionnaireReactContext, context => context.getQuestionById({questionId}));
    const questionnaire = useContextSelector(QuestionnaireReactContext, context => context.questionnaire);
    const activeQuestionId = useContextSelector(QuestionnaireReactContext, context => context.activeQuestionId);
    const [questionText, setQuestionText] = useState(get(question, 'text', ''));
    const debouncedQuestionText = useDebounce({value: questionText, delay: 500});
    const [questionType, setQuestionType] = useState(get(question, 'type', ''));
    const [imageExpanded, setImageExpanded] = useState(!!get(question, 'questionMediaUrl'));
    const [isImageUploading, setIsImageUploading] = useState(false);

    const isSurvey = get(questionnaire, 'type') === SignDocTypes.SURVEY;
    const signDocId = get(questionnaire, 'id');
    const isActive = activeQuestionId === questionId;
    
    const updateQuestionTextWrapper = (e) => {
        setQuestionText(e.target.value)
    }
    const updateQuestionTypeWrapper = (e) => {
        setQuestionType(e.target.value)
    }

    useEffect(() => {
        if (activeQuestionId === questionId) {
            updateQuestion({type: questionType, text: debouncedQuestionText})
        }
    }, [debouncedQuestionText, questionType, activeQuestionId, questionId, updateQuestion]);

    const createQuestionTypesArray = () => {
        const availableTypes = isSurvey ? surveyQuestionTypes : examQuestionTypes;
        
        return availableTypes.map((key) => ({
            value: key,
            text: i18n.translate(`questionnaireQuestions.questionOptions.${key}`)
        }));
    };
    
    const onFocusHandler = (e) => {
        e.target.select()
    }
    const innerOnFocus = (e) => {
        e.stopPropagation()
        onFocus(questionId)
    }

    const currentQuestionType = get(question, 'type');
    const shouldShowOptions = questionsTypesToShowOptions.includes(currentQuestionType);
    const shouldShowRatingScale = currentQuestionType === questionnaireQuestionTypes.RATING_SCALE;
    const hasImage = !!get(question, 'questionMediaUrl');

    const handleImageChange = useCallback(({ imageUrl }) => {
        updateQuestion({ questionMediaUrl: imageUrl });
    }, [updateQuestion]);

    const handleLoadingChange = useCallback(({ isLoading }) => {
        setIsImageUploading(isLoading);
    }, []);

    return (
        <div
            className={`question-form-container ${isActive ? 'focused' : ''}`}
            onClick={innerOnFocus}
            data-testid="question-form-container"
        >
            <div className="question-header">
                <FormInput
                    id={`question-text-${questionId}`}
                    placeholder={i18n.translate('questionnaireQuestions.placeHolders.question')}
                    required
                    style={{width: '70%'}}
                    dontShowErrorMessage
                    value={questionText}
                    onChange={updateQuestionTextWrapper}
                    multiline={true}
                    minRows={2}
                    maxRows={4}
                    onFocus={onFocusHandler}
                    dataTestId="question-text-input"
                />

                {signDocId && (
                    <button
                        className={`add-image-button ${hasImage ? 'has-image' : ''} ${imageExpanded ? 'expanded' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!isImageUploading) {
                                setImageExpanded(!imageExpanded);
                            }
                        }}
                        title={i18n.translate('questionnaireQuestions.addImage')}
                        data-testid="add-image-button"
                        type="button"
                        disabled={isImageUploading}
                    >
                        <ImageIcon />
                    </button>
                )}

                <div className="select-box">
                    <SelectBox
                        className="selectQuestionType"
                        options={createQuestionTypesArray()}
                        onChange={updateQuestionTypeWrapper}
                        value={questionType}
                        dataTestId="question-type-select"
                    />
                </div>
            </div>

            {signDocId && imageExpanded && (
                <div className="question-image-inline">
                    <QuestionImageUpload
                        signDocId={signDocId}
                        questionId={questionId}
                        initialImageUrl={get(question, 'questionMediaUrl')}
                        onImageChange={handleImageChange}
                        onLoadingChange={handleLoadingChange}
                    />
                </div>
            )}
            
            {shouldShowOptions && (
                <QuestionOptions questionId={questionId} />
            )}
            
            {shouldShowRatingScale && (
                <RatingScaleOptions questionId={questionId} />
            )}
            
            {questionnaireQuestionTypes.FREE_TEXT === currentQuestionType && (
                <div className="no-options" data-testid="free-text-placeholder">
                    {i18n.translate(`questionnaireQuestions.placeHolders.${currentQuestionType}`)}
                </div>
            )}
            
            {isActive && (
                <div className="footer">
                    <div className="ButtonsRow">
                        <Button
                            className="button"
                            color={buttonColors.GREY}
                            size="small"
                            icon={<DeleteForeverIcon/>}
                            onClick={deleteQuestion}
                            dataTestId="delete-question-button"
                        />
                        <Button
                            className="button"
                            color={buttonColors.GREY}
                            size="small"
                            icon={<ContentCopyIcon/>}
                            onClick={duplicateQuestion}
                            dataTestId="duplicate-question-button"
                        />
                        <Button
                            color={buttonColors.GREY}
                            size="small"
                            icon={<AddCircleOutlineIcon/>}
                            onClick={addQuestion}
                            dataTestId="add-question-button"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};